// pages/secure/PayrollPage.js
// Payroll front end for the gross-to-net engine: employee tax profiles, pay
// periods, the run, payslips and stubs, year-to-date, and the 941 / W-2 previews.
//
// Rewritten for the new engine. The previous page was built against the old
// estimate-only module and called /api/payroll/items, which no longer exists.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';
import PayrollTaxTables from '../../components/secure/PayrollTaxTables';

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '');
const iso = (d) => (d ? String(d).slice(0, 10) : '');

const STATUS_TONE = {
    open: 'bg-slate-100 text-slate-700',
    calculated: 'bg-blue-100 text-blue-700',
    approved: 'bg-violet-100 text-violet-700',
    posted: 'bg-green-100 text-green-700',
};

const TABS = [
    ['periods', 'Pay Runs'],
    ['employees', 'Employees'],
    ['payslips', 'Payslips & Stubs'],
    ['ytd', 'Year to Date'],
    ['reports', '941 / W-2'],
    ['tax', 'Tax Tables'],
];

const PayrollPage = ({ user }) => {
    const api = useApiClient();
    const [tab, setTab] = useState('periods');
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [busy, setBusy] = useState(false);

    const [periods, setPeriods] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [payslips, setPayslips] = useState([]);
    const [ytd, setYtd] = useState([]);
    const [reports, setReports] = useState({ q941: null, w2: null });

    const [detail, setDetail] = useState(null);      // a period + its items
    const [periodForm, setPeriodForm] = useState(null);
    const [empForm, setEmpForm] = useState(null);
    const [slipForm, setSlipForm] = useState(null);
    const [trace, setTrace] = useState(null);

    const year = new Date().getFullYear();
    const note = (m) => { setFlash(m); setTimeout(() => setFlash(''), 5000); };
    const fail = (e) => setError(e.message || String(e));

    const load = useCallback(() => {
        setError('');
        if (tab === 'periods') api.get('/api/payroll/periods').then(setPeriods).catch(fail);
        if (tab === 'employees') api.get('/api/payroll/employees').then(setEmployees).catch(fail);
        if (tab === 'payslips') api.get(`/api/payroll/payslips?year=${year}`).then(setPayslips).catch(fail);
        if (tab === 'ytd') api.get(`/api/payroll/ytd?year=${year}`).then(setYtd).catch(fail);
        if (tab === 'reports') {
            Promise.all([
                api.get(`/api/payroll/reports/941?year=${year}`),
                api.get(`/api/payroll/reports/w2?year=${year}`),
            ]).then(([q941, w2]) => setReports({ q941, w2 })).catch(fail);
        }
    }, [api, tab, year]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (id) => {
        try { setDetail(await api.get(`/api/payroll/periods/${id}`)); } catch (e) { fail(e); }
    };
    const refreshDetail = async (id) => setDetail(await api.get(`/api/payroll/periods/${id}`));

    // ---- actions -----------------------------------------------------------
    const savePeriod = async () => {
        setBusy(true); setError('');
        try {
            if (periodForm.period_id) await api.put(`/api/payroll/periods/${periodForm.period_id}`, periodForm);
            else await api.post('/api/payroll/periods', periodForm);
            setPeriodForm(null); note('Pay run saved.'); load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const calculate = async (id, force = false) => {
        setBusy(true); setError('');
        try {
            const r = await api.post(`/api/payroll/periods/${id}/calculate`, force ? { force_unverified: true } : {});
            note(r.message + (r.provisional ? ' (provisional — some tax constants are unsigned)' : ''));
            await refreshDetail(id); load();
        } catch (e) {
            // 412 means the tax table is not signed off; offer the sandbox path.
            if (/not fully signed off/i.test(e.message || '')) {
                if (window.confirm(`${e.message}\n\nRun anyway as a provisional calculation?`)) return calculate(id, true);
            } else fail(e);
        } finally { setBusy(false); }
    };

    const postPeriod = async (id) => {
        if (!window.confirm('Post this pay run to the general ledger?\n\nThis writes one balanced journal per payslip and cannot be edited afterwards without unposting.')) return;
        setBusy(true);
        try {
            const r = await api.post(`/api/payroll/periods/${id}/post`);
            note(r.message); await refreshDetail(id); load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const unpostPeriod = async (id) => {
        if (!window.confirm('Unpost this run?\n\nThe ledger entries are voided, not deleted, and the run becomes editable again.')) return;
        setBusy(true);
        try {
            const r = await api.post(`/api/payroll/periods/${id}/unpost`);
            note(r.message); await refreshDetail(id); load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const removePeriod = async (p) => {
        if (!window.confirm(`Remove the pay run dated ${fmtDate(p.pay_date)}?\n\nThe record is retained for the retention register.`)) return;
        try { const r = await api.delete(`/api/payroll/periods/${p.period_id}`); note(r.message); setDetail(null); load(); }
        catch (e) { fail(e); }
    };

    const saveEmployee = async () => {
        setBusy(true); setError('');
        try {
            await api.put(`/api/payroll/employees/${empForm.employee_id}`, empForm);
            setEmpForm(null); note('Employee payroll profile saved.'); load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const saveSlip = async () => {
        setBusy(true); setError('');
        try {
            const r = await api.put(`/api/payroll/payslips/${slipForm.item_id}`, slipForm);
            note(`Payslip updated — net ${money(r.net_pay)}.`);
            setSlipForm(null);
            if (detail) await refreshDetail(detail.period_id);
            load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const removeSlip = async (s) => {
        if (!window.confirm(`Remove the payslip for ${s.employee_name}?\n\nThe record is retained.`)) return;
        try {
            const r = await api.delete(`/api/payroll/payslips/${s.item_id}`);
            note(r.message);
            if (detail) await refreshDetail(detail.period_id);
        } catch (e) { fail(e); }
    };

    // Stubs stream as a PDF rather than JSON, so they are fetched with the auth
    // header and opened from a blob URL. The alternative — passing the JWT as a
    // query parameter so window.open can carry it — writes the token into every
    // access log and proxy along the way, which is not worth the convenience.
    const openStub = async (id) => {
        setError('');
        try {
            const res = await fetch(`/api/payroll/payslips/${id}/stub`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
            });
            if (!res.ok) throw new Error(`Could not load the stub (${res.status}).`);
            const url = URL.createObjectURL(await res.blob());
            window.open(url, '_blank', 'noopener');
            // Give the new tab time to take the blob before revoking it.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { fail(e); }
    };

    const showTrace = (slip) => {
        let d = slip.detail_json;
        if (typeof d === 'string') { try { d = JSON.parse(d); } catch { d = null; } }
        setTrace({ name: slip.employee_name, lines: (d && d.trace) || [], deductions: (d && d.deductions) || [] });
    };

    // ---- shared bits -------------------------------------------------------
    const btn = 'text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300';
    const ghost = 'text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50';
    const input = 'mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm';
    const label = 'block text-xs font-medium text-slate-600';
    const th = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500';
    const td = 'px-3 py-2 text-sm';
    const num = `${td} text-right tabular-nums`;

    return (
        <div className="p-6 max-w-7xl">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Payroll</h1>
                    <p className="text-slate-500 text-sm mt-0.5">
                        Gross-to-net with Illinois withholding, FICA wage-base caps, and employer taxes. Posts to the general ledger.
                    </p>
                    {/* Job access (owner, 26 Oct 2026): the Accounting job views this page; changes stay with the managing broker. */}
                    {Array.isArray(user?.areas) && !user?.isManagingBroker && (
                        <p className="mt-1 text-sm text-amber-800 bg-amber-50 rounded px-2 py-1">View only: your job access lets you look, not change. The managing broker makes changes here.</p>
                    )}
                </div>
                {tab === 'periods' && (
                    <button className={btn} onClick={() => setPeriodForm({ frequency: 'monthly', period_start: '', period_end: '', pay_date: '' })}>
                        + New pay run
                    </button>
                )}
            </div>

            <div className="flex gap-4 border-b border-slate-200 mt-4 mb-5 overflow-x-auto">
                {TABS.map(([id, lbl]) => (
                    <button key={id} onClick={() => setTab(id)}
                        className={`px-1 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                            tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                        {lbl}
                    </button>
                ))}
            </div>

            {error && <div className="mb-4 px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}
            {flash && <div className="mb-4 px-3 py-2 rounded border border-green-200 bg-green-50 text-sm text-green-700">{flash}</div>}

            {/* ---------------- PAY RUNS ---------------- */}
            {tab === 'periods' && (
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <DataList rows={periods} rowKey="period_id" csvName="payroll-pay-runs" defaultSort={{ key: 'pay_date', dir: 'desc' }} empty="No pay runs yet."
                        filters={[{ key: 'status', label: 'Status' }, { key: 'frequency', label: 'Freq' }]}
                        columns={[
                            { key: 'pay_date', label: 'Pay date', type: 'date', render: (p) => <span className="font-medium">{fmtDate(p.pay_date)}</span> },
                            { key: 'period', label: 'Period', value: (p) => `${fmtDate(p.period_start)} – ${fmtDate(p.period_end)}`, sortValue: (p) => p.period_start, render: (p) => <span className="text-slate-500">{fmtDate(p.period_start)} – {fmtDate(p.period_end)}</span> },
                            { key: 'frequency', label: 'Freq' },
                            { key: 'status', label: 'Status', render: (p) => <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_TONE[p.status]}`}>{p.status}</span> },
                            { key: 'employees', label: 'Employees', type: 'number' },
                            { key: 'total_gross', label: 'Gross', type: 'number', render: (p) => <span className="tabular-nums">{money(p.total_gross)}</span> },
                            { key: 'total_net', label: 'Net', type: 'number', render: (p) => <span className="tabular-nums">{money(p.total_net)}</span> },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (p) => (
                                <span className="whitespace-nowrap">
                                    <button onClick={() => openDetail(p.period_id)} className="text-blue-600 hover:underline mr-3">Open</button>
                                    {p.status !== 'posted' && <button onClick={() => removePeriod(p)} className="text-red-600 hover:underline">Remove</button>}
                                </span>) },
                        ]} />
                </div>
            )}

            {/* ---------------- EMPLOYEES ---------------- */}
            {tab === 'employees' && (
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <DataList rows={employees} rowKey="employee_id" csvName="payroll-employees" defaultSort={{ key: 'name', dir: 'asc' }} empty="No employees."
                        filters={[{ key: 'pay_type', label: 'Type' }, { key: 'pay_schedule', label: 'Schedule' }]}
                        columns={[
                            { key: 'name', label: 'Name', value: (e) => `${e.first_name || ''} ${e.last_name || ''} ${e.job_title || ''}`.trim(), sortValue: (e) => `${e.last_name || ''} ${e.first_name || ''}`,
                                render: (e) => <span className="font-medium">{e.first_name} {e.last_name}<div className="text-xs text-slate-400">{e.job_title}</div></span> },
                            { key: 'pay_type', label: 'Type', value: (e) => (e.pay_type === 'none' ? 'not paid' : e.pay_type), render: (e) => (e.pay_type === 'none' ? <span className="text-slate-400">not paid</span> : e.pay_type) },
                            { key: 'pay_rate', label: 'Rate', type: 'number', render: (e) => <span className="tabular-nums">{e.pay_rate ? money(e.pay_rate) : '—'}{e.pay_type === 'salary' && e.pay_rate ? <div className="text-xs text-slate-400">per year</div> : null}</span> },
                            { key: 'pay_schedule', label: 'Schedule' },
                            { key: 'fed_filing_status', label: 'Fed status' },
                            { key: 'il_allowances', label: 'IL allow.', value: (e) => `${e.il_allowances ?? ''}${Number(e.il_additional_allowances) ? ` + ${e.il_additional_allowances} addl` : ''}` },
                            { key: 'ssn', label: 'SSN', value: (e) => (e.ssn_last4 ? `•••• ${e.ssn_last4}` : 'missing'), render: (e) => (e.ssn_last4 ? `•••• ${e.ssn_last4}` : <span className="text-amber-600 text-xs">missing</span>) },
                            { key: 'edit', label: '', sortable: false, csv: false, search: false, render: (e) => <button onClick={() => setEmpForm({ ...e })} className="text-blue-600 hover:underline">Edit</button> },
                        ]} />
                </div>
            )}

            {/* ---------------- PAYSLIPS ---------------- */}
            {tab === 'payslips' && (
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <DataList rows={payslips} rowKey="item_id" csvName={`payroll-payslips-${year}`} defaultSort={{ key: 'pay_date', dir: 'desc' }} empty={`No payslips for ${year}.`}
                        filters={[{ key: 'employee_name', label: 'Employee' }]}
                        columns={[
                            { key: 'pay_date', label: 'Pay date', type: 'date', render: (s) => fmtDate(s.pay_date) },
                            { key: 'employee_name', label: 'Employee', render: (s) => <span className="font-medium">{s.employee_name}</span> },
                            { key: 'gross_pay', label: 'Gross', type: 'number', render: (s) => <span className="tabular-nums">{money(s.gross_pay)}</span> },
                            { key: 'federal_tax', label: 'Federal', type: 'number', render: (s) => <span className="tabular-nums">{money(s.federal_tax)}</span> },
                            { key: 'state_tax', label: 'IL', type: 'number', render: (s) => <span className="tabular-nums">{money(s.state_tax)}</span> },
                            { key: 'fica', label: 'FICA', type: 'number', value: (s) => Number(s.social_security) + Number(s.medicare) + Number(s.addl_medicare), render: (s) => <span className="tabular-nums">{money(Number(s.social_security) + Number(s.medicare) + Number(s.addl_medicare))}</span> },
                            { key: 'net_pay', label: 'Net', type: 'number', render: (s) => <span className="tabular-nums font-semibold">{money(s.net_pay)}</span> },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (s) => (
                                <span className="whitespace-nowrap">
                                    <button onClick={() => openStub(s.item_id)} className="text-blue-600 hover:underline mr-3">Stub</button>
                                    <button onClick={() => showTrace(s)} className="text-slate-500 hover:underline">Why?</button>
                                </span>) },
                        ]} />
                </div>
            )}

            {/* ---------------- YTD ---------------- */}
            {tab === 'ytd' && (
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <DataList rows={ytd} rowKey="employee_id" csvName={`payroll-ytd-${year}`} defaultSort={{ key: 'employee_name', dir: 'asc' }} empty={`Nothing for ${year} yet.`}
                        columns={[
                            { key: 'employee_name', label: 'Employee', render: (r) => <span className="font-medium">{r.employee_name}</span> },
                            { key: 'payslips', label: 'Slips', type: 'number' },
                            { key: 'ytd_gross', label: 'Gross', type: 'number', render: (r) => <span className="tabular-nums">{money(r.ytd_gross)}</span> },
                            { key: 'ytd_fed', label: 'Federal', type: 'number', render: (r) => <span className="tabular-nums">{money(r.ytd_fed)}</span> },
                            { key: 'ytd_il', label: 'IL', type: 'number', render: (r) => <span className="tabular-nums">{money(r.ytd_il)}</span> },
                            { key: 'ytd_ss', label: 'SS', type: 'number', render: (r) => <span className="tabular-nums">{money(r.ytd_ss)}</span> },
                            { key: 'ytd_mc', label: 'Medicare', type: 'number', render: (r) => <span className="tabular-nums">{money(r.ytd_mc)}</span> },
                            { key: 'ytd_net', label: 'Net', type: 'number', render: (r) => <span className="tabular-nums font-semibold">{money(r.ytd_net)}</span> },
                            { key: 'ytd_futa', label: 'FUTA', type: 'number', render: (r) => <span className="tabular-nums">{money(r.ytd_futa)}</span> },
                            { key: 'ytd_suta', label: 'SUTA', type: 'number', render: (r) => <span className="tabular-nums">{money(r.ytd_suta)}</span> },
                        ]} />
                </div>
            )}

            {/* ---------------- REPORTS ---------------- */}
            {tab === 'reports' && (
                <div className="space-y-6">
                    {reports.q941 && (
                        <section className="bg-white border border-slate-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-slate-100">
                                <h2 className="font-semibold text-slate-800">Form 941 — {reports.q941.year}</h2>
                                <p className="text-xs text-slate-500 mt-1">{reports.q941.note}</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-slate-50 border-b border-slate-200"><tr>
                                        <th className={th}>Quarter</th><th className={`${th} text-right`}>L1 employees</th>
                                        <th className={`${th} text-right`}>L2 wages</th><th className={`${th} text-right`}>L3 federal</th>
                                        <th className={`${th} text-right`}>L5a SS tax</th><th className={`${th} text-right`}>L5c Medicare</th>
                                        <th className={`${th} text-right`}>L6 total</th>
                                    </tr></thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {reports.q941.quarters.map(q => (
                                            <tr key={q.quarter}>
                                                <td className={`${td} font-medium`}>Q{q.quarter}</td>
                                                <td className={num}>{q.line1_employees}</td>
                                                <td className={num}>{money(q.line2_wages)}</td>
                                                <td className={num}>{money(q.line3_federal_withheld)}</td>
                                                <td className={num}>{money(q.line5a_ss_tax)}</td>
                                                <td className={num}>{money(q.line5c_medicare_tax)}</td>
                                                <td className={`${num} font-semibold`}>{money(q.line6_total_taxes)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {reports.w2 && (
                        <section className="bg-white border border-slate-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-slate-100">
                                <h2 className="font-semibold text-slate-800">W-2 preview — {reports.w2.year}</h2>
                                <p className="text-xs text-slate-500 mt-1">{reports.w2.note}</p>
                            </div>
                            <div className="overflow-x-auto">
                                <div className="p-3"><DataList rows={reports.w2.employees} rowKey="employee_id" csvName={`w2-boxes-${year}`} defaultSort={{ key: 'name', dir: 'asc' }} empty="No W-2 figures for this year."
                                    columns={[
                                        { key: 'name', label: 'Employee', value: (e) => `${e.first_name} ${e.last_name}`, sortValue: (e) => `${e.last_name} ${e.first_name}`, render: (e) => <span className="font-medium">{e.first_name} {e.last_name}</span> },
                                        { key: 'ssn', label: 'SSN', value: (e) => (e.ssn_last4 ? `XXX-XX-${e.ssn_last4}` : '—') },
                                        ...['box1', 'box2', 'box3', 'box4', 'box5', 'box6', 'box16', 'box17'].map((b) => ({ key: b, label: `Box ${b.slice(3)}`, type: 'number', render: (e) => money(e[b]) })),
                                    ]} /></div>
                            </div>
                        </section>
                    )}
                </div>
            )}

            {/* ---------------- TAX TABLES ---------------- */}
            {tab === 'tax' && <PayrollTaxTables user={user} />}

            {/* ---------------- PERIOD DETAIL ---------------- */}
            <Modal open={!!detail} onClose={() => setDetail(null)} size="lg"
                title={detail ? `Pay run — ${fmtDate(detail.pay_date)}` : ''}
                footer={detail && (
                    <div className="flex items-center gap-3 w-full">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_TONE[detail.status]}`}>{detail.status}</span>
                        <div className="ml-auto flex gap-2">
                            {detail.status !== 'posted' && (
                                <>
                                    <button className={ghost} disabled={busy} onClick={() => calculate(detail.period_id)}>
                                        {busy ? 'Working…' : 'Calculate'}
                                    </button>
                                    <button className={btn} disabled={busy || !detail.items.length} onClick={() => postPeriod(detail.period_id)}>
                                        Post to ledger
                                    </button>
                                </>
                            )}
                            {detail.status === 'posted' && user?.isManagingBroker && (
                                <button className={ghost} disabled={busy} onClick={() => unpostPeriod(detail.period_id)}>Unpost</button>
                            )}
                        </div>
                    </div>
                )}>
                {detail && (
                    <div>
                        <div className="text-sm text-slate-600 mb-3">
                            Period {fmtDate(detail.period_start)} – {fmtDate(detail.period_end)} · {detail.frequency}
                            {detail.note ? ` · ${detail.note}` : ''}
                        </div>
                        <div className="border border-slate-200 rounded-md overflow-x-auto">
                            <div className="p-3"><DataList rows={detail.items} rowKey="item_id" csvName={`payroll-period-${detail.period_id}`} defaultSort={{ key: 'employee_name', dir: 'asc' }} empty="Nothing calculated yet — use Calculate."
                                columns={[
                                    { key: 'employee_name', label: 'Employee', render: (s) => <span className="font-medium">{s.employee_name}</span> },
                                    { key: 'gross_pay', label: 'Gross', type: 'number', render: (s) => money(s.gross_pay) },
                                    { key: 'federal_tax', label: 'Fed', type: 'number', render: (s) => money(s.federal_tax) },
                                    { key: 'state_tax', label: 'IL', type: 'number', render: (s) => money(s.state_tax) },
                                    { key: 'social_security', label: 'SS', type: 'number', render: (s) => money(s.social_security) },
                                    { key: 'medicare', label: 'MC', type: 'number', render: (s) => money(s.medicare) },
                                    { key: 'net_pay', label: 'Net', type: 'number', render: (s) => <span className="font-semibold">{money(s.net_pay)}</span> },
                                    { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (s) => (
                                        <span className="whitespace-nowrap">
                                            <button onClick={() => showTrace(s)} className="text-slate-500 hover:underline mr-2">Why?</button>
                                            <button onClick={() => openStub(s.item_id)} className="text-blue-600 hover:underline mr-2">Stub</button>
                                            {detail.status !== 'posted' && (
                                                <>
                                                    <button onClick={() => setSlipForm({ ...s })} className="text-blue-600 hover:underline mr-2">Edit</button>
                                                    <button onClick={() => removeSlip(s)} className="text-red-600 hover:underline">Remove</button>
                                                </>
                                            )}
                                        </span>) },
                                ]} /></div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* ---------------- CALCULATION TRACE ---------------- */}
            <Modal open={!!trace} onClose={() => setTrace(null)} size="lg" title={trace ? `How ${trace.name}'s pay was calculated` : ''}
                footer={<button className={ghost} onClick={() => setTrace(null)}>Close</button>}>
                {trace && (
                    <div>
                        <ol className="text-sm text-slate-700 space-y-1.5 list-decimal pl-5">
                            {trace.lines.map((l, i) => <li key={i} className="leading-relaxed">{l}</li>)}
                        </ol>
                        {!trace.lines.length && <p className="text-slate-400 text-sm">No trace stored for this payslip.</p>}
                        {!!trace.deductions.length && (
                            <div className="mt-4">
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Deductions</h4>
                                <ul className="text-sm text-slate-700">
                                    {trace.deductions.map((d, i) => (
                                        <li key={i}>{d.label} — {money(d.amount)} ({d.pre_tax ? 'pre-tax' : 'post-tax'}{d.reduces_fica ? ', reduces FICA' : ''})</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* ---------------- PERIOD FORM ---------------- */}
            <Modal open={!!periodForm} onClose={() => setPeriodForm(null)} size="md"
                title={periodForm?.period_id ? 'Edit pay run' : 'New pay run'}
                footer={<>
                    <button className={ghost} onClick={() => setPeriodForm(null)}>Cancel</button>
                    <button className={btn} disabled={busy} onClick={savePeriod}>{busy ? 'Saving…' : 'Save'}</button>
                </>}>
                {periodForm && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={label}>Period start</label>
                                <input type="date" className={input} value={iso(periodForm.period_start)}
                                    onChange={e => setPeriodForm({ ...periodForm, period_start: e.target.value })} /></div>
                            <div><label className={label}>Period end</label>
                                <input type="date" className={input} value={iso(periodForm.period_end)}
                                    onChange={e => setPeriodForm({ ...periodForm, period_end: e.target.value })} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={label}>Pay date</label>
                                <input type="date" className={input} value={iso(periodForm.pay_date)}
                                    onChange={e => setPeriodForm({ ...periodForm, pay_date: e.target.value })} /></div>
                            <div><label className={label}>Frequency</label>
                                <select className={input} value={periodForm.frequency}
                                    onChange={e => setPeriodForm({ ...periodForm, frequency: e.target.value })}>
                                    {['weekly', 'biweekly', 'semimonthly', 'monthly'].map(f => <option key={f}>{f}</option>)}
                                </select></div>
                        </div>
                        <div><label className={label}>Note</label>
                            <input className={input} value={periodForm.note || ''}
                                onChange={e => setPeriodForm({ ...periodForm, note: e.target.value })} /></div>
                        <p className="text-xs text-slate-500">
                            The wage-base caps key on the pay date, so calculate runs oldest first.
                        </p>
                    </div>
                )}
            </Modal>

            {/* ---------------- EMPLOYEE FORM ---------------- */}
            <Modal open={!!empForm} onClose={() => setEmpForm(null)} size="lg"
                title={empForm ? `Payroll profile — ${empForm.first_name} ${empForm.last_name}` : ''}
                footer={<>
                    <button className={ghost} onClick={() => setEmpForm(null)}>Cancel</button>
                    <button className={btn} disabled={busy} onClick={saveEmployee}>{busy ? 'Saving…' : 'Save'}</button>
                </>}>
                {empForm && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                            <div><label className={label}>Pay type</label>
                                <select className={input} value={empForm.pay_type || 'none'}
                                    onChange={e => setEmpForm({ ...empForm, pay_type: e.target.value })}>
                                    <option value="none">not paid</option><option value="salary">salary</option><option value="hourly">hourly</option>
                                </select></div>
                            <div><label className={label}>Rate {empForm.pay_type === 'salary' ? '(ANNUAL)' : '(per hour)'}</label>
                                <input type="number" step="0.01" className={input} value={empForm.pay_rate ?? ''}
                                    onChange={e => setEmpForm({ ...empForm, pay_rate: e.target.value })} /></div>
                            <div><label className={label}>Schedule</label>
                                <select className={input} value={empForm.pay_schedule || 'monthly'}
                                    onChange={e => setEmpForm({ ...empForm, pay_schedule: e.target.value })}>
                                    {['weekly', 'biweekly', 'semimonthly', 'monthly'].map(f => <option key={f}>{f}</option>)}
                                </select></div>
                        </div>
                        {empForm.pay_type === 'salary' && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                Salary rate is the ANNUAL figure. $3,000 a month is entered as 36000.
                            </p>
                        )}

                        <div className="grid grid-cols-4 gap-3">
                            <div><label className={label}>SSN last 4</label>
                                <input maxLength={4} className={input} value={empForm.ssn_last4 || ''}
                                    onChange={e => setEmpForm({ ...empForm, ssn_last4: e.target.value })} /></div>
                            <div><label className={label}>Worker type</label>
                                <select className={input} value={empForm.worker_type || 'W2'}
                                    onChange={e => setEmpForm({ ...empForm, worker_type: e.target.value })}>
                                    <option>W2</option><option>1099</option>
                                </select></div>
                            <div className="col-span-2"><label className={label}>Hire date</label>
                                <input type="date" className={input} value={iso(empForm.hire_date)}
                                    onChange={e => setEmpForm({ ...empForm, hire_date: e.target.value })} /></div>
                        </div>

                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Form W-4</h4>
                            <div className="grid grid-cols-3 gap-3">
                                <div><label className={label}>Filing status</label>
                                    <select className={input} value={empForm.fed_filing_status}
                                        onChange={e => setEmpForm({ ...empForm, fed_filing_status: e.target.value })}>
                                        <option value="single">single</option><option value="married_joint">married filing jointly</option>
                                        <option value="head_household">head of household</option>
                                    </select></div>
                                <div><label className={label}>Step 3 dependents ($)</label>
                                    <input type="number" step="0.01" className={input} value={empForm.fed_dependent_credit ?? 0}
                                        onChange={e => setEmpForm({ ...empForm, fed_dependent_credit: e.target.value })} /></div>
                                <div><label className={label}>Step 4(c) extra ($)</label>
                                    <input type="number" step="0.01" className={input} value={empForm.fed_extra_withholding ?? 0}
                                        onChange={e => setEmpForm({ ...empForm, fed_extra_withholding: e.target.value })} /></div>
                            </div>
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                <div><label className={label}>Step 4(a) other income</label>
                                    <input type="number" step="0.01" className={input} value={empForm.fed_other_income ?? 0}
                                        onChange={e => setEmpForm({ ...empForm, fed_other_income: e.target.value })} /></div>
                                <div><label className={label}>Step 4(b) deductions</label>
                                    <input type="number" step="0.01" className={input} value={empForm.fed_deductions ?? 0}
                                        onChange={e => setEmpForm({ ...empForm, fed_deductions: e.target.value })} /></div>
                                <div className="flex items-end gap-4 pb-2">
                                    <label className="flex items-center gap-1.5 text-sm">
                                        <input type="checkbox" checked={!!Number(empForm.fed_multiple_jobs)}
                                            onChange={e => setEmpForm({ ...empForm, fed_multiple_jobs: e.target.checked ? 1 : 0 })} />
                                        Step 2 box
                                    </label>
                                    <label className="flex items-center gap-1.5 text-sm">
                                        <input type="checkbox" checked={!!Number(empForm.fed_exempt)}
                                            onChange={e => setEmpForm({ ...empForm, fed_exempt: e.target.checked ? 1 : 0 })} />
                                        Exempt
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Form IL-W-4</h4>
                            <div className="grid grid-cols-3 gap-3">
                                <div><label className={label}>Basic allowances</label>
                                    <input type="number" className={input} value={empForm.il_allowances ?? 0}
                                        onChange={e => setEmpForm({ ...empForm, il_allowances: e.target.value })} /></div>
                                <div><label className={label}>Additional (age 65+ / blind)</label>
                                    <input type="number" className={input} value={empForm.il_additional_allowances ?? 0}
                                        onChange={e => setEmpForm({ ...empForm, il_additional_allowances: e.target.value })} /></div>
                                <div><label className={label}>Extra withholding ($)</label>
                                    <input type="number" step="0.01" className={input} value={empForm.il_extra_withholding ?? 0}
                                        onChange={e => setEmpForm({ ...empForm, il_extra_withholding: e.target.value })} /></div>
                            </div>
                        </div>

                        <div className="grid grid-cols-4 gap-3">
                            <div className="col-span-2"><label className={label}>Street</label>
                                <input className={input} value={empForm.street_address || ''}
                                    onChange={e => setEmpForm({ ...empForm, street_address: e.target.value })} /></div>
                            <div><label className={label}>City</label>
                                <input className={input} value={empForm.city || ''}
                                    onChange={e => setEmpForm({ ...empForm, city: e.target.value })} /></div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><label className={label}>State</label>
                                    <input className={input} value={empForm.state || ''}
                                        onChange={e => setEmpForm({ ...empForm, state: e.target.value })} /></div>
                                <div><label className={label}>ZIP</label>
                                    <input className={input} value={empForm.zip_code || ''}
                                        onChange={e => setEmpForm({ ...empForm, zip_code: e.target.value })} /></div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* ---------------- PAYSLIP EDIT ---------------- */}
            <Modal open={!!slipForm} onClose={() => setSlipForm(null)} size="md"
                title={slipForm ? `Adjust payslip — ${slipForm.employee_name}` : ''}
                footer={<>
                    <button className={ghost} onClick={() => setSlipForm(null)}>Cancel</button>
                    <button className={btn} disabled={busy} onClick={saveSlip}>{busy ? 'Saving…' : 'Save'}</button>
                </>}>
                {slipForm && (
                    <div className="space-y-3">
                        <p className="text-xs text-slate-500">
                            Net pay is recalculated from these figures and cannot be typed directly. The payslip is flagged as manually edited.
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                            {[['gross_pay', 'Gross'], ['federal_tax', 'Federal'], ['state_tax', 'Illinois'],
                              ['social_security', 'Social Security'], ['medicare', 'Medicare'], ['addl_medicare', 'Addl Medicare'],
                              ['pretax_deductions', 'Pre-tax deductions'], ['other_deductions', 'Post-tax deductions'],
                              ['other_pay', 'Other pay']].map(([k, lbl]) => (
                                <div key={k}><label className={label}>{lbl}</label>
                                    <input type="number" step="0.01" className={input} value={slipForm[k] ?? 0}
                                        onChange={e => setSlipForm({ ...slipForm, [k]: e.target.value })} /></div>
                            ))}
                        </div>
                        <div><label className={label}>Memo</label>
                            <input className={input} value={slipForm.memo || ''}
                                onChange={e => setSlipForm({ ...slipForm, memo: e.target.value })} /></div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PayrollPage;
